import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    LinearProgress, 
    Typography,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Tooltip,
    Fab
} from '@mui/material';
import {
    PlayArrow as StartIcon,
    Download as DownloadIcon,
    LocationCity as CityIcon,
    Stop as StopIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

interface ProcessingStep {
    label: string;
    completed: boolean;
    active: boolean;
}

interface EnhancedActionButtonsProps {
    salesReport: string;
    archiveOrders: string;
    isLoading: boolean;
    isProcessed: boolean;
    processingProgress?: number;
    onStart: () => void;
    onDownload: () => void;
    onOpenCityManager: () => void;
    onCancel?: () => void;
    onReset?: () => void;
}

const processingSteps = [
    'Загрузка Sales Report',
    'Загрузка Archive Orders', 
    'Сопоставление данных',
    'Обработка адресов',
    'Расчет сумм',
    'Готово к скачиванию'
];

const EnhancedActionButtons: React.FC<EnhancedActionButtonsProps> = ({
    salesReport,
    archiveOrders,
    isLoading,
    isProcessed,
    processingProgress = 0,
    onStart,
    onDownload,
    onOpenCityManager,
    onCancel,
    onReset
}) => {
    const [currentStep, setCurrentStep] = useState(0);

    const getProcessingSteps = (): ProcessingStep[] => {
        const progress = processingProgress || 0;
        const stepProgress = progress / 100 * (processingSteps.length - 1);
        
        return processingSteps.map((label, index) => ({
            label,
            completed: index < Math.floor(stepProgress),
            active: index === Math.floor(stepProgress)
        }));
    };

    const isButtonDisabled = !salesReport || !archiveOrders || isLoading;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
            {/* Основные кнопки действий */}
            <Card variant="outlined">
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: isLoading ? 2 : 0 }}>
                        <Button
                            variant="contained"
                            size="large"
                            disabled={isButtonDisabled}
                            onClick={isProcessed ? onDownload : onStart}
                            startIcon={isProcessed ? <DownloadIcon /> : <StartIcon />}
                            sx={{
                                minWidth: 140,
                                bgcolor: isProcessed ? 'success.main' : 'primary.main',
                                '&:hover': {
                                    bgcolor: isProcessed ? 'success.dark' : 'primary.dark',
                                },
                                '&:disabled': {
                                    bgcolor: 'action.disabledBackground'
                                }
                            }}
                        >
                            {isLoading ? 'Обработка...' : isProcessed ? 'Скачать' : 'Начать'}
                        </Button>

                        {isLoading && onCancel && (
                            <Tooltip title="Отменить обработку">
                                <IconButton 
                                    color="error" 
                                    onClick={onCancel}
                                    sx={{ ml: 1 }}
                                >
                                    <StopIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        {isProcessed && onReset && (
                            <Tooltip title="Начать заново">
                                <IconButton 
                                    color="primary" 
                                    onClick={onReset}
                                    sx={{ ml: 1 }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Box sx={{ flex: 1 }} />

                        <Typography variant="body2" color="text.secondary">
                            {!salesReport && 'Выберите Sales Report'}
                            {salesReport && !archiveOrders && 'Выберите Archive Orders'}
                            {salesReport && archiveOrders && !isLoading && !isProcessed && 'Готово к обработке'}
                            {isProcessed && 'Обработка завершена'}
                        </Typography>
                    </Box>

                    {/* Прогресс-бар и шаги */}
                    {isLoading && (
                        <Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={processingProgress}
                                sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    mb: 2,
                                    bgcolor: 'action.hover',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 4
                                    }
                                }}
                            />
                            <Typography variant="body2" color="text.secondary" align="center">
                                {Math.round(processingProgress)}% завершено
                            </Typography>
                        </Box>
                    )}

                    {/* Детальные шаги (показываем только при обработке) */}
                    {isLoading && (
                        <Box sx={{ mt: 2 }}>
                            <Stepper activeStep={currentStep} orientation="vertical">
                                {getProcessingSteps().map((step, index) => (
                                    <Step key={step.label} completed={step.completed}>
                                        <StepLabel
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    fontSize: '0.875rem',
                                                    color: step.active ? 'primary.main' : 
                                                           step.completed ? 'success.main' : 'text.secondary'
                                                }
                                            }}
                                        >
                                            {step.label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Плавающая кнопка управления городами */}
            {localStorage.getItem('role') === 'admin' && (
                <Tooltip title="Управление городами" placement="left">
                    <Fab
                        color="primary"
                        onClick={onOpenCityManager}
                        sx={{ 
                            position: 'fixed', 
                            right: 24, 
                            bottom: 24,
                            zIndex: 1000
                        }}
                    >
                        <CityIcon />
                    </Fab>
                </Tooltip>
            )}
        </Box>
    );
};

export default EnhancedActionButtons;
